import { DispositifBadge } from '../DispositifBadge';
/**
 * Section "Simulations en cours" (DOMIUM + BATICO/RCDS) avec actions de signature et facture.
 * Extrait de DocumentSections.js (refactor P2 — Feb 2026).
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DomiumParametresCarte } from './DomiumParametresCarte';
import {
  Clock, Calendar, ShieldCheck, FileCheck, FileText, Pencil, Users,
  Upload, Eye, Download, PlayCircle, Trash2,
} from 'lucide-react';

export default function SimulationsSection({
  items, onResume, selection, onToggle, onDeleteOne,
  onUploadDomiumSignature, onDownloadDocumentFacture,
  onViewManualSignature, onDownloadManualSignature,
  onDownloadDossierMaitre, onUploadCpcgSigned, onViewCpcgSigned, onDownloadCpcgSigned,
  onEditChantierInfo, onManageIntervenants,
}) {
  if (!items?.length) return null;
  const isSelected = (id) => selection?.has?.(`simulation:${id}`);
  return (
    <div>
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4 text-orange-500" />
        Simulations en cours ({items.length})
        <span className="text-xs font-normal text-gray-500">(max 30 jours)</span>
      </h4>
      <div className="space-y-3">
        {items.map((sim) => {
          const isDomium = sim.type === 'simulation_domium' || sim.source === 'domium_subscriptions';
          const isBatico = !isDomium && (
            sim.source === 'souscriptions'
            || sim.type === 'simulation'
            || sim.type === 'simulation_batico'
            || sim.type === 'simulation_archos'
            || sim.type === 'simulation_nominative'
            || sim.type === 'simulation_avenant'
          );
          const canManualSign = isDomium || isBatico;
          return (
          <Card key={sim.id} className={`border-orange-200 bg-orange-50/50 transition ${isSelected(sim.id) ? 'ring-2 ring-orange-400' : ''}`} data-testid={`simulation-card-${sim.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-[260px]">
                  {onToggle && (
                    <input type="checkbox" checked={isSelected(sim.id)}
                      onChange={() => onToggle({ type: 'simulation', id: sim.id, label: sim.nom || sim.type || 'Simulation' })}
                      className="mt-1 h-4 w-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500 cursor-pointer flex-shrink-0"
                      aria-label={`Sélectionner la simulation ${sim.id}`} data-testid={`select-simulation-${sim.id}`} />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 flex items-center gap-2">{sim.nom || sim.type || 'Simulation RCDS'}<DispositifBadge code={sim.dispositif} testId={`dispositif-badge-sim-${sim.id}`} /></p>
                    <p className="text-sm text-gray-600">
                      Créé le {new Date(sim.date_creation).toLocaleDateString('fr-FR')}
                      {sim.montant_ttc && ` • ${Number(sim.montant_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € Montant Total`}
                      {sim.montant_ht && !sim.montant_ttc && ` • ${sim.montant_ht.toLocaleString()}€ HT`}
                    </p>
                    {isDomium && (sim.date_ouverture_chantier || sim.date_reception_previsionnelle) && (
                      <p className="text-xs text-gray-600 mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`dates-recap-${sim.id}`}>
                        <Calendar className="h-3 w-3 text-indigo-500 flex-shrink-0" />
                        {sim.date_ouverture_chantier && (
                          <span>
                            <span className="text-gray-500">Ouverture :</span>{' '}
                            <strong className="text-gray-800">{new Date(sim.date_ouverture_chantier).toLocaleDateString('fr-FR')}</strong>
                          </span>
                        )}
                        {sim.date_reception_previsionnelle && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>
                              <span className="text-gray-500">Réception&nbsp;:</span>{' '}
                              <strong className="text-gray-800">{new Date(sim.date_reception_previsionnelle).toLocaleDateString('fr-FR')}</strong>
                            </span>
                            <span className={'inline-flex items-center text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ' + (sim.date_reception_is_calculee ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')}
                              title={sim.date_reception_is_calculee ? 'Date dérivée automatiquement de la durée des travaux. À confirmer via « Compléter infos chantier ».' : 'Date saisie explicitement par le cooper / souscripteur.'}
                              data-testid={`reception-status-${sim.id}`}>
                              {sim.date_reception_is_calculee ? 'calculée' : 'confirmée'}
                            </span>
                          </>
                        )}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-orange-600 border-orange-300">
                        <Clock className="h-3 w-3 mr-1" />Étape {sim.etape_actuelle || sim.etape_courante || 1}
                      </Badge>
                      {sim.jours_restants !== undefined && (
                        <Badge variant="outline" className={sim.jours_restants <= 7 ? "text-red-600 border-red-300" : "text-amber-600 border-amber-300"}>
                          {sim.jours_restants}j restants
                        </Badge>
                      )}
                      {canManualSign && sim.is_signed && (
                        <Badge variant="outline" className="text-white border-emerald-800 bg-emerald-700 inline-flex items-center pr-1 shadow-sm font-semibold" data-testid={`badge-signed-${sim.id}`}>
                          <ShieldCheck className="h-3 w-3 mr-1" />Devis signé{sim.signature_method ? ` (${sim.signature_method === 'manuscrit_uploaded' ? 'manuscrit' : 'électronique'})` : ''}
                          {sim.signature_method === 'manuscrit_uploaded' && (
                            <span className="inline-flex items-center gap-0.5 ml-1.5 pl-1.5 border-l border-emerald-500/60">
                              <button type="button" onClick={(e) => { e.stopPropagation(); onViewManualSignature && onViewManualSignature(sim); }} title="Visualiser le fichier signé manuscritement" aria-label="Visualiser le fichier signé manuscritement"
                                className="p-0.5 rounded hover:bg-emerald-800 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid={`btn-view-manual-sig-${sim.id}`}>
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); onDownloadManualSignature && onDownloadManualSignature(sim); }} title="Télécharger le fichier signé manuscritement" aria-label="Télécharger le fichier signé manuscritement"
                                className="p-0.5 rounded hover:bg-emerald-800 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid={`btn-download-manual-sig-${sim.id}`}>
                                <Download className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )}
                        </Badge>
                      )}
                      {isDomium && sim.cpcg_signed_uploaded && (
                        <Badge variant="outline" className="text-white border-teal-800 bg-teal-700 inline-flex items-center pr-1 shadow-sm font-semibold" data-testid={`badge-cpcg-signed-${sim.id}`}>
                          <FileCheck className="h-3 w-3 mr-1" />CP/CG signé reçu
                          <span className="inline-flex items-center gap-0.5 ml-1.5 pl-1.5 border-l border-teal-500/60">
                            <button type="button" onClick={(e) => { e.stopPropagation(); onViewCpcgSigned && onViewCpcgSigned(sim); }} title="Visualiser les CP/CG signés" aria-label="Visualiser les CP/CG signés"
                              className="p-0.5 rounded hover:bg-teal-800 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-200" data-testid={`btn-view-cpcg-signed-${sim.id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onDownloadCpcgSigned && onDownloadCpcgSigned(sim); }} title="Télécharger les CP/CG signés" aria-label="Télécharger les CP/CG signés"
                              className="p-0.5 rounded hover:bg-teal-800 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-teal-200" data-testid={`btn-download-cpcg-signed-${sim.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {isDomium && onEditChantierInfo && (
                    <Button variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => onEditChantierInfo(sim)} title="Compléter ou rectifier les informations chantier (permis, dates, type de travaux…)" data-testid={`btn-edit-chantier-info-${sim.id}`}>
                      <Pencil className="h-4 w-4 mr-1" /> Compléter infos chantier
                    </Button>
                  )}
                  {isDomium && onManageIntervenants && (
                    <Button variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={() => onManageIntervenants(sim)} title="Gérer les entreprises et bureaux d'études prenant part à l'opération" data-testid={`btn-manage-intervenants-${sim.id}`}>
                      <Users className="h-4 w-4 mr-1" /> Intervenants
                    </Button>
                  )}
                  {isDomium && onDownloadDossierMaitre && (
                    <Button variant="outline" size="sm" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => onDownloadDossierMaitre(sim)} title="Télécharger le Dossier Maître (CP + CG + Note) à transmettre pour signature manuscrite" data-testid={`btn-download-cpcg-${sim.id}`}>
                      <FileText className="h-4 w-4 mr-1" /> Télécharger CP/CG
                    </Button>
                  )}
                  {isDomium && onUploadCpcgSigned && (
                    <Button variant="outline" size="sm" className="border-teal-300 text-teal-700 hover:bg-teal-50"
                      onClick={() => onUploadCpcgSigned(sim)} title={sim.cpcg_signed_uploaded ? 'Remplacer les CP/CG signés' : 'Téléverser les CP/CG signés manuscritement'} data-testid={`btn-upload-cpcg-${sim.id}`}>
                      <Upload className="h-4 w-4 mr-1" /> {sim.cpcg_signed_uploaded ? 'Remplacer CP/CG signé' : 'Téléverser CP/CG signé'}
                    </Button>
                  )}
                  {canManualSign && onUploadDomiumSignature && (
                    <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => onUploadDomiumSignature(sim)} title={sim.is_signed ? 'Remplacer la signature manuscrite' : 'Téléverser le devis signé manuscritement'} data-testid={`btn-upload-signature-${sim.id}`}>
                      <Upload className="h-4 w-4 mr-1" /> {sim.is_signed ? 'Remplacer signature' : 'Téléverser signature'}
                    </Button>
                  )}
                  {canManualSign && sim.is_signed && onDownloadDocumentFacture && (
                    <Button size="sm" className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                      onClick={() => onDownloadDocumentFacture(sim)} title="Télécharger le document valant facture" data-testid={`btn-download-facture-${sim.id}`}>
                      <FileCheck className="h-4 w-4 mr-1" /> Document valant facture
                    </Button>
                  )}
                  {sim.can_resume !== false && onResume && (
                    <Button onClick={() => onResume(sim)} className="bg-orange-500 hover:bg-orange-600">
                      <PlayCircle className="h-4 w-4 mr-2" />Reprendre
                    </Button>
                  )}
                  {onDeleteOne && (
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onDeleteOne({ type: 'simulation', id: sim.id, label: sim.nom || sim.type || 'Simulation' })}
                      title="Supprimer cette simulation" data-testid={`delete-simulation-${sim.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              {isDomium && <DomiumParametresCarte sim={sim} />}
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
}
